import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatINR, formatApiErrorDetail } from "@/api/client";
import { useCart } from "@/contexts/CartContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import OrderTimeline from "@/components/orders/OrderTimeline";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, X, RotateCcw, MessageCircle, Repeat, Truck, MapPin, Copy,
  IndianRupee, Phone, CalendarClock, ShieldCheck,
} from "lucide-react";

const STATUS_COLOR = {
  placed: "bg-blue-100 text-blue-700",
  confirmed: "bg-blue-100 text-blue-700",
  packed: "bg-indigo-100 text-indigo-700",
  shipped: "bg-purple-100 text-purple-700",
  out_for_delivery: "bg-amber-100 text-amber-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  return_requested: "bg-amber-100 text-amber-700",
  returned: "bg-amber-100 text-amber-700",
  refunded: "bg-green-100 text-green-700",
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { settings } = useSiteSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [returnReason, setReturnReason] = useState("");

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load order");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchOrder(); /* eslint-disable-next-line */ }, [id]);

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/cancel`, { reason: cancelReason });
      toast.success("Order cancelled");
      setCancelOpen(false); setCancelReason("");
      fetchOrder();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const doReturn = async () => {
    if (!returnReason.trim()) { toast.error("Please tell us why you want to return"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/return`, { reason: returnReason });
      toast.success("Return requested");
      setReturnOpen(false); setReturnReason("");
      fetchOrder();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Return request failed");
    } finally { setBusy(false); }
  };

  const doReorder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/reorder`);
      data.items.forEach((it) => addItem({
        id: it.product_id, name: it.name, price: it.price, mrp: it.mrp,
        images: [it.image], unit: it.unit, seller_name: it.seller_name,
      }, it.qty));
      if (data.out_of_stock?.length) {
        toast.warning(`Skipped (out of stock): ${data.out_of_stock.join(", ")}`);
      } else {
        toast.success("Items added to cart");
      }
      navigate("/cart");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reorder failed");
    } finally { setBusy(false); }
  };

  const downloadInvoice = () => {
    const token = localStorage.getItem("fah_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/invoice.html`;
    // Open in new tab with Authorization header — easiest is fetch then write
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const w = window.open("", "_blank");
        if (!w) { toast.error("Allow pop-ups to download invoice"); return; }
        w.document.write(html);
        w.document.close();
      })
      .catch(() => toast.error("Could not load invoice"));
  };

  const copyTracking = () => {
    if (!order?.tracking_id) return;
    navigator.clipboard.writeText(order.tracking_id);
    toast.success("Tracking ID copied");
  };

  if (loading) return <div className="container-fah py-16 text-center text-muted-foreground">Loading order…</div>;
  if (!order) return <div className="container-fah py-16 text-center text-muted-foreground">Order not found.</div>;

  const canCancel = !["shipped", "out_for_delivery", "delivered", "cancelled", "returned", "refunded"].includes(order.status);
  const canReturn = order.status === "delivered";

  // primary seller for WhatsApp contact
  const sellerName = order.items[0]?.seller_name || "Seller";
  const sellerWa = (settings?.whatsapp_number || "918090245875").replace(/\D/g, "");

  return (
    <div className="container-fah py-6 sm:py-10" data-testid="page-order-detail">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="link-back">
        <ArrowLeft className="w-4 h-4" /> Back to My Orders
      </Link>

      {/* HEADER */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Order Number</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-0.5" data-testid="order-detail-number">{order.order_number}</h1>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" /> Placed on {new Date(order.created_at).toLocaleString("en-IN")}
          </div>
        </div>
        <span className={`text-xs font-bold uppercase px-3 py-1.5 rounded-full ${STATUS_COLOR[order.status] || "bg-muted text-foreground"}`} data-testid="order-status-badge">
          {order.status.replace(/_/g, " ")}
        </span>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        {/* LEFT COLUMN */}
        <div className="space-y-5">
          {/* Timeline */}
          <OrderTimeline status={order.status} timeline={order.timeline || []} />

          {/* Tracking */}
          {(order.tracking_id || order.estimated_delivery) && (
            <div className="card-fah p-5" data-testid="order-tracking">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5 text-primary" />
                <h3 className="font-display font-bold">Shipment Tracking</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {order.courier_name && <Field label="Courier" value={order.courier_name} />}
                {order.tracking_id && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Tracking ID</div>
                    <div className="font-mono font-semibold mt-0.5 flex items-center gap-2">
                      {order.tracking_id}
                      <button onClick={copyTracking} className="text-muted-foreground hover:text-primary" data-testid="btn-copy-tracking"><Copy className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
                {order.estimated_delivery && <Field label="Estimated Delivery" value={new Date(order.estimated_delivery).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} />}
                {order.delivered_at && <Field label="Delivered On" value={new Date(order.delivered_at).toLocaleString("en-IN")} />}
              </div>
              {order.tracking_url && (
                <a href={order.tracking_url} target="_blank" rel="noreferrer" className="mt-3 inline-block bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="link-track-courier">
                  Track on {order.courier_name || "Courier"} →
                </a>
              )}
            </div>
          )}

          {/* Items */}
          <div className="card-fah p-5" data-testid="order-items">
            <h3 className="font-display font-bold mb-3">Items ({order.items.length})</h3>
            <div className="space-y-3">
              {order.items.map((it, i) => (
                <div key={i} className="flex gap-3 pb-3 border-b border-border last:border-0 last:pb-0" data-testid={`order-item-${i}`}>
                  <Link to={`/product/${it.product_id}`} className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-muted shrink-0">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{it.brand}</div>
                    <Link to={`/product/${it.product_id}`} className="font-semibold text-sm hover:text-primary line-clamp-2">{it.name}</Link>
                    <div className="text-xs text-muted-foreground mt-0.5">SKU: <span className="font-mono">{it.sku || "—"}</span> · Sold by {it.seller_name}</div>
                    <div className="mt-1 text-sm">
                      <span className="font-bold">{formatINR(it.price)}</span>
                      {it.mrp > it.price && <span className="ml-2 text-xs text-muted-foreground line-through">{formatINR(it.mrp)}</span>}
                      <span className="ml-2 text-xs text-muted-foreground">× {it.qty}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 font-bold whitespace-nowrap">{formatINR(it.price * it.qty)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Address */}
          <div className="card-fah p-5" data-testid="order-address">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-primary" />
              <h3 className="font-display font-bold">Delivery Address</h3>
            </div>
            <div className="text-sm">
              <div className="font-semibold">{order.address?.full_name || order.buyer_name}</div>
              <div className="flex items-center gap-1.5 text-muted-foreground mt-0.5 text-xs"><Phone className="w-3.5 h-3.5" />{order.address?.phone}</div>
              <div className="mt-2 text-muted-foreground leading-relaxed">
                {order.address?.line1}{order.address?.line2 ? `, ${order.address.line2}` : ""}<br />
                {order.address?.city}{order.address?.district ? `, ${order.address.district}` : ""}{order.address?.state ? `, ${order.address.state}` : ""} — {order.address?.pincode}
                {order.address?.landmark && <><br /><span className="text-xs">Landmark: {order.address.landmark}</span></>}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-4">
          {/* Payment summary */}
          <div className="card-fah p-5" data-testid="order-payment">
            <div className="flex items-center gap-2 mb-3"><IndianRupee className="w-5 h-5 text-primary" /><h3 className="font-display font-bold">Payment</h3></div>
            <div className="space-y-1.5 text-sm">
              <Row label="Method" value={(order.payment_method || "").toUpperCase()} />
              <Row label="Status" value={<span className="font-semibold uppercase">{order.payment_status}</span>} />
              <div className="border-t border-border my-2" />
              <Row label="Subtotal" value={formatINR(order.subtotal)} />
              <Row label="Shipping" value={formatINR(order.shipping || 0)} />
              <Row label="Handling Charges" value={formatINR(order.handling || 0)} />
              <div className="border-t border-border my-2" />
              <Row label={<b>Total</b>} value={<b className="text-lg">{formatINR(order.total)}</b>} />
            </div>
          </div>

          {/* Actions */}
          <div className="card-fah p-5 space-y-2.5" data-testid="order-actions">
            <h3 className="font-display font-bold mb-1">Need help with this order?</h3>
            <ActionBtn icon={FileText} label="Download Invoice" onClick={downloadInvoice} testid="btn-download-invoice" />
            <ActionBtn icon={Repeat} label="Reorder Items" onClick={doReorder} testid="btn-reorder" disabled={busy} />
            <a
              href={`https://wa.me/${sellerWa}?text=${encodeURIComponent(`Hi, I need help with order ${order.order_number} from ${sellerName}.`)}`}
              target="_blank" rel="noreferrer"
              className="w-full flex items-center justify-center gap-2 border border-border bg-white hover:bg-muted text-sm font-semibold py-2.5 rounded-lg"
              data-testid="btn-contact-seller"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" /> Contact Seller on WhatsApp
            </a>
            {canCancel && (
              <button onClick={() => setCancelOpen(true)} className="w-full flex items-center justify-center gap-2 border border-destructive/30 text-destructive hover:bg-destructive/10 text-sm font-semibold py-2.5 rounded-lg" data-testid="btn-cancel-order">
                <X className="w-4 h-4" /> Cancel Order
              </button>
            )}
            {canReturn && (
              <button onClick={() => setReturnOpen(true)} className="w-full flex items-center justify-center gap-2 border border-amber-300 text-amber-700 hover:bg-amber-50 text-sm font-semibold py-2.5 rounded-lg" data-testid="btn-return-order">
                <RotateCcw className="w-4 h-4" /> Request Return
              </button>
            )}
            <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-2 border-t border-border">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
              <span>Secure checkout · 7-day return policy after delivery · Buyer protection</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel modal */}
      {cancelOpen && (
        <Modal title="Cancel Order" onClose={() => setCancelOpen(false)} testid="cancel-modal">
          <p className="text-sm text-muted-foreground mb-3">We&apos;re sorry to see you cancel. Tell us why (optional):</p>
          <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} placeholder="Reason for cancellation…" className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="cancel-reason" />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setCancelOpen(false)} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted">Keep Order</button>
            <button disabled={busy} onClick={doCancel} className="bg-destructive text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50" data-testid="cancel-confirm">
              {busy ? "Cancelling…" : "Yes, Cancel"}
            </button>
          </div>
        </Modal>
      )}

      {/* Return modal */}
      {returnOpen && (
        <Modal title="Request Return" onClose={() => setReturnOpen(false)} testid="return-modal">
          <p className="text-sm text-muted-foreground mb-3">Return window: 7 days from delivery. Please describe the issue:</p>
          <textarea value={returnReason} onChange={(e) => setReturnReason(e.target.value)} rows={4} placeholder="What went wrong with this order?" className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white" data-testid="return-reason" />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setReturnOpen(false)} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted">Cancel</button>
            <button disabled={busy} onClick={doReturn} className="bg-amber-600 text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50" data-testid="return-confirm">
              {busy ? "Submitting…" : "Request Return"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div><div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div><div className="font-semibold mt-0.5">{value}</div></div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>
);
const ActionBtn = ({ icon: Icon, label, onClick, testid, disabled = false }) => (
  <button onClick={onClick} disabled={disabled} className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50" data-testid={testid}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);
const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid={testid}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-lg">{title}</h2>
        <button onClick={onClose} className="p-1.5 hover:bg-muted rounded"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default OrderDetail;
